<script setup lang="ts">
/**
 * Listado de presupuestos (§47).
 *
 * Calcado del de recibos, que a su vez sale del **invoice list** de la
 * plantilla. Dos diferencias, y las dos vienen del documento: la etiqueta que se
 * muestra la resuelve el BACKEND —porque `expired` depende del día local del
 * centro (§47.2)— y el alta permite crear la ficha del cliente ahí mismo: la
 * mitad de los presupuestos son para alguien que todavía no vino.
 */
import { type ClientPayload, createClient, listClients } from '@/api/clients'
import type { DiveCenterClient } from '@/types'
import {
  type Quote,
  type QuoteDisplayStatus,
  listQuotes,
} from '@/api/quotes'
import AlertBox from '@/components/AlertBox.vue'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { usePreferences } from '@/composables/usePreferences'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'

definePage({ meta: { requiresAuth: true } })

const router = useRouter()
const { currentId } = useCurrentDiveCenter()
const { formatCurrency, formatDate } = usePreferences()

const quotes = ref<Quote[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref('')

const search = ref('')
const statusFilter = ref<QuoteDisplayStatus | null>(null)
const page = ref(1)
const itemsPerPage = ref(25)

/**
 * Los seis estados que se ven. Cuatro se guardan y dos se derivan (§47.2), pero
 * acá da igual: la etiqueta viene resuelta y esto solo la pinta.
 */
const STATUS_CHIP: Record<QuoteDisplayStatus, { label: string; color: string; icon: string }> = {
  draft: { label: 'Draft', color: 'secondary', icon: 'ri-draft-line' },
  sent: { label: 'Sent', color: 'info', icon: 'ri-mail-send-line' },
  approved: { label: 'Approved', color: 'success', icon: 'ri-checkbox-circle-line' },
  rejected: { label: 'Rejected', color: 'error', icon: 'ri-close-circle-line' },
  expired: { label: 'Expired', color: 'warning', icon: 'ri-time-line' },

  // Morado y no verde: pegado a Approved en la misma tira, se confundían.
  converted: { label: 'Converted', color: '#7E57C2', icon: 'ri-calendar-check-line' },
  partially_converted: { label: 'Partly converted', color: 'warning', icon: 'ri-calendar-2-line' },
}

const headers = [
  { title: '#', key: 'number' },
  { title: 'Client', key: 'clientName' },
  { title: 'Issued', key: 'createdAt' },
  { title: 'Valid until', key: 'validUntil' },
  { title: 'Status', key: 'displayStatus', sortable: false },
  { title: 'Total', key: 'total', align: 'end' as const },
  { title: '', key: 'actions', sortable: false, width: 80, align: 'end' as const },
]

/** Los contadores de la tira, sobre la etiqueta que se ve. */
const TILES: QuoteDisplayStatus[] = ['draft', 'sent', 'approved', 'converted']

const counts = computed(() =>
  Object.fromEntries(
    TILES.map(s => [s, quotes.value.filter(q => q.displayStatus === s).length]),
  ) as Record<QuoteDisplayStatus, number>,
)

async function load() {
  const dcId = currentId.value

  if (!dcId)
    return

  loading.value = true
  error.value = ''
  try {
    const res = await listQuotes(dcId, {
      status: statusFilter.value ?? undefined,
      search: search.value.trim() || undefined,
      limit: itemsPerPage.value,
      skip: (page.value - 1) * itemsPerPage.value,
    })

    quotes.value = res.quotes
    total.value = res.total
  }
  catch (e) {
    error.value = apiMessage(e, 'Could not load quotes.')
    quotes.value = []
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

/*
 * Es una pantalla de la CUENTA en el menú pero sus datos son del centro, y el
 * `:key` del RouterView no la remonta al cambiar de centro (§23.3).
 */
watch(currentId, load)

// Buscar y filtrar vuelven a la primera página: quedarse en la 3 de un
// resultado que ahora tiene una sola muestra una tabla vacía.
watch([search, statusFilter], () => {
  page.value = 1
  load()
})

watch([page, itemsPerPage], load)

// ---- Alta ----

const newDialog = ref(false)
const clientSearch = ref('')
const clients = ref<DiveCenterClient[]>([])
const clientsLoading = ref(false)

let clientTimer: number | undefined

watch(clientSearch, q => {
  if (clientTimer)
    clearTimeout(clientTimer)

  // Mismo debounce que el buscador de la libreta.
  clientTimer = window.setTimeout(async () => {
    clientsLoading.value = true
    try {
      const res = await listClients({ search: q?.trim() || undefined, limit: 20 })

      clients.value = res.clients
    }
    finally {
      clientsLoading.value = false
    }
  }, 350)
})

/**
 * El alta rápida de ficha, dentro del mismo diálogo.
 *
 * Un presupuesto se pide muchas veces por mail, antes de que la persona haya
 * pisado el centro: mandar a crear la ficha en otra pantalla y volver es el paso
 * que se olvida. Son los cuatro campos del walk-in del roster; el resto de la
 * ficha se completa cuando venga.
 */
const creating = ref(false)
const savingClient = ref(false)

const newClient = reactive<Pick<ClientPayload, 'firstName' | 'lastName' | 'email' | 'phone'>>({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
})

/*
 * El nombre es lo único obligatorio, y lo dice el propio campo: un botón apagado
 * no explica qué falta.
 */
const clientFormRef = ref()
const requiredRule = (v: string) => (!!v && !!v.trim()) || 'Required.'

function openNew() {
  clientSearch.value = ''
  clients.value = []
  creating.value = false
  newClient.firstName = ''
  newClient.lastName = ''
  newClient.email = ''
  newClient.phone = ''
  newDialog.value = true

  // Primera carga sin escribir nada: la libreta entera es el punto de partida,
  // igual que el picker del roster (§20.10).
  clientSearch.value = ' '
  clientSearch.value = ''
}

/**
 * Abre el editor con ese cliente, **sin crear nada**.
 *
 * Igual que el recibo: el borrador vive en memoria hasta que se guarda, así que
 * equivocarse de cliente y cerrar la pantalla no deja un documento tirado en el
 * listado.
 */
function startQuote(client: DiveCenterClient) {
  newDialog.value = false
  router.push(`/sales/quotes/new?clientId=${client.id}`)
}

async function createAndStart() {
  const { valid } = await clientFormRef.value.validate()

  if (!valid)
    return

  savingClient.value = true
  try {
    const { client } = await createClient({
      firstName: newClient.firstName.trim(),
      lastName: newClient.lastName.trim(),
      email: newClient.email?.trim() || undefined,
      phone: newClient.phone?.trim() || undefined,

      // Queda en el centro en el que se está trabajando: un presupuesto de este
      // mostrador es de este centro.
      diveCenterIds: currentId.value ? [currentId.value] : undefined,
    })

    notifySuccess('Client added.')
    startQuote(client)
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not create the client.'))
  }
  finally {
    savingClient.value = false
  }
}

const money = (amount: number, currency: string) => formatCurrency(amount, currency)

const clientLabel = (c: DiveCenterClient) => `${c.firstName} ${c.lastName ?? ''}`.trim()

/*
 * Un borrador se abre para SEGUIR ARMÁNDOLO; uno ya enviado, para mirarlo y
 * decidir. Mandar los dos al mismo lado obligaría a uno a dar un rodeo.
 */
const quoteTo = (q: Quote) =>
  q.status === 'draft' ? `/sales/quotes/${q.id}` : `/sales/quotes/${q.id}/preview`
</script>

<template>
  <div>
    <AlertBox
      variant="danger"
      :message="error"
    />

    <VCard class="mb-6">
      <VCardText class="d-flex flex-wrap ga-6">
        <div
          v-for="s in TILES"
          :key="s"
          class="d-flex align-center ga-3"
        >
          <VAvatar
            :color="STATUS_CHIP[s].color"
            variant="tonal"
            rounded
          >
            <VIcon :icon="STATUS_CHIP[s].icon" />
          </VAvatar>
          <div>
            <div class="text-h6">
              {{ counts[s] }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ STATUS_CHIP[s].label }}
            </div>
          </div>
        </div>
      </VCardText>
    </VCard>

    <VCard>
      <VCardText class="d-flex flex-wrap align-center ga-4">
        <!--
          Los seis que se ven, derivados incluidos: el backend los traduce a lo
          que sí está en la base (§47.2). "Approved" quiere decir aprobado y NADA
          más — en cuanto tiene una reserva pasa a (a medio) convertido, que es
          lo que dice la tira de arriba.
        -->
        <VSelect
          v-model="statusFilter"
          :items="[
            { title: 'All', value: null },
            { title: 'Draft', value: 'draft' },
            { title: 'Sent', value: 'sent' },
            { title: 'Expired', value: 'expired' },
            { title: 'Approved', value: 'approved' },
            { title: 'Partly converted', value: 'partially_converted' },
            { title: 'Converted', value: 'converted' },
            { title: 'Rejected', value: 'rejected' },
          ]"
          label="Status"
          density="compact"
          hide-details
          style="min-inline-size: 200px; max-inline-size: 240px;"
        />

        <VSpacer />

        <VTextField
          v-model="search"
          placeholder="Search by number or client"
          prepend-inner-icon="ri-search-line"
          density="compact"
          clearable
          hide-details
          style="min-inline-size: 200px; max-inline-size: 280px;"
        />

        <VBtn
          prepend-icon="ri-add-line"
          @click="openNew"
        >
          New quote
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :items="quotes"
        :items-length="total"
        :headers="headers"
        :loading="loading"
        item-value="id"
        class="text-no-wrap"
        @update:options="() => {}"
      >
        <template #item.number="{ item }">
          <!--
            Un borrador no tiene número: se reserva al enviar. Guión en vez de
            celda vacía, que se lee como un dato que no cargó.
          -->
          <RouterLink
            :to="quoteTo(item)"
            class="font-weight-medium"
          >
            {{ item.number ?? '—' }}
          </RouterLink>
        </template>

        <!--
          A la ficha, abierta en Activities: la pregunta que sigue a ver un
          presupuesto es qué más hizo esa persona (§38.3.1). Sin ficha queda
          el nombre congelado y nada que abrir.
        -->
        <template #item.clientName="{ item }">
          <RouterLink
            v-if="item.clientId"
            :to="`/clients/${item.clientId}`"
          >
            {{ item.clientName }}
          </RouterLink>
          <span v-else>{{ item.clientName }}</span>
        </template>

        <template #item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <template #item.validUntil="{ item }">
          {{ item.validUntil ? formatDate(item.validUntil) : '—' }}
        </template>

        <template #item.displayStatus="{ item }">
          <VChip
            :color="STATUS_CHIP[item.displayStatus].color"
            size="small"
          >
            {{ STATUS_CHIP[item.displayStatus].label }}
          </VChip>
        </template>

        <template #item.total="{ item }">
          <span class="font-weight-medium">{{ money(item.total, item.currency) }}</span>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            icon="ri-eye-line"
            variant="text"
            size="small"
            :to="quoteTo(item)"
            title="Open"
          />
        </template>

        <template #no-data>
          <div class="text-center text-medium-emphasis py-8">
            No quotes yet.
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <!-- Alta: elegir el cliente, o crearlo acá mismo. -->
    <VDialog
      v-model="newDialog"
      max-width="560"
      scrollable
    >
      <VCard>
        <VCardItem>
          <VCardTitle>New quote</VCardTitle>
          <VCardSubtitle>
            It starts empty — pick services and rentals from the catalogue.
          </VCardSubtitle>
        </VCardItem>

        <VDivider />

        <VCardText v-if="!creating">
          <VTextField
            v-model="clientSearch"
            placeholder="Search the client book"
            prepend-inner-icon="ri-search-line"
            clearable
            :loading="clientsLoading"
            class="mb-4"
          />

          <VList v-if="clients.length">
            <VListItem
              v-for="c in clients"
              :key="c.id"
              :title="clientLabel(c)"
              :subtitle="c.email || undefined"
              @click="startQuote(c)"
            >
              <template #append>
                <VIcon icon="ri-arrow-right-line" />
              </template>
            </VListItem>
          </VList>

          <p
            v-else-if="!clientsLoading"
            class="text-body-2 text-medium-emphasis"
          >
            No clients found.
          </p>

          <VBtn
            variant="tonal"
            prepend-icon="ri-user-add-line"
            class="mt-2"
            @click="creating = true"
          >
            New client
          </VBtn>
        </VCardText>

        <!--
          Los cuatro campos del walk-in. El resto de la ficha se completa cuando
          la persona venga: pedirle todo a alguien que solo pidió un precio es
          pedirle demasiado.

          🪤 `pt-6`: el diálogo es `scrollable`, así que el contenido se recorta
          contra la cabecera y el label flotante del primer campo quedaba pisado.
          Es el mismo cepo del `VWindow` de §3.7 — un label que flota hacia
          arriba necesita aire arriba.
        -->
        <VForm
          v-else
          ref="clientFormRef"
          @submit.prevent="createAndStart"
        >
          <VCardText class="pt-6">
            <VRow>
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="newClient.firstName"
                  label="First name"
                  :rules="[requiredRule]"
                  autofocus
                />
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="newClient.lastName"
                  label="Last name"
                />
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="newClient.email"
                  label="Email"
                  type="email"
                  hint="Used to send the quote."
                  persistent-hint
                />
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="newClient.phone"
                  label="Phone"
                />
              </VCol>
            </VRow>
          </VCardText>

          <!--
            El submit vive acá dentro para que Enter en cualquier campo cree y
            siga: es un alta de cuatro campos, y obligar a llevar la mano al
            botón es un gesto de más en el flujo que vino a acortar. El botón
            que se ve es el de las acciones del diálogo.
          -->
          <VBtn
            type="submit"
            class="d-none"
          />
        </VForm>

        <VDivider />

        <VCardActions class="justify-end">
          <VBtn
            variant="text"
            @click="creating ? (creating = false) : (newDialog = false)"
          >
            {{ creating ? 'Back' : 'Cancel' }}
          </VBtn>
          <VBtn
            v-if="creating"
            :loading="savingClient"
            @click="createAndStart"
          >
            Create and continue
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
